import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { getEstimates } from '../../../utils/estimateStorage';
import { format, parseISO, isWithinInterval, subMonths } from 'date-fns';

const ScenarioComparisonMatrix = () => {
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [dateRange, setDateRange] = useState('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const allEstimates = getEstimates();

  const filteredEstimates = useMemo(() => {
    let filtered = [...allEstimates];

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch(dateRange) {
        case '30days':
          startDate = subMonths(now, 1);
          break;
        case '90days':
          startDate = subMonths(now, 3);
          break;
        case '6months':
          startDate = subMonths(now, 6);
          break;
        case '1year':
          startDate = subMonths(now, 12);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered?.filter(est => {
          const estDate = parseISO(est?.timestamp);
          return isWithinInterval(estDate, { start: startDate, end: now });
        });
      }
    }

    // Project type filter (delivery mode)
    if (projectTypeFilter !== 'all') {
      filtered = filtered?.filter(est => est?.deliveryMode === projectTypeFilter);
    }

    // Sort
    switch(sortBy) {
      case 'date':
        filtered?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp));
        break;
      case 'value':
        filtered?.sort((a, b) => (b?.calculations?.sellPrice || 0) - (a?.calculations?.sellPrice || 0));
        break;
      case 'margin':
        filtered?.sort((a, b) => (b?.calculations?.profitMarginPercentage || 0) - (a?.calculations?.profitMarginPercentage || 0));
        break;
      default:
        break;
    }

    return filtered;
  }, [allEstimates, dateRange, projectTypeFilter, sortBy]);

  const comparisonData = useMemo(() => {
    if (selectedScenarios?.length === 0) return null;

    const scenarios = selectedScenarios?.map(id => 
      filteredEstimates?.find(est => est?.id === id)
    )?.filter(Boolean);

    const avgSellPrice = scenarios?.reduce((sum, s) => sum + (s?.calculations?.sellPrice || 0), 0) / scenarios?.length;
    const avgTotalCost = scenarios?.reduce((sum, s) => sum + (s?.calculations?.totalCost || 0), 0) / scenarios?.length;
    const avgGrossProfit = scenarios?.reduce((sum, s) => sum + (s?.calculations?.grossProfit || 0), 0) / scenarios?.length;
    const avgMargin = scenarios?.reduce((sum, s) => sum + (s?.calculations?.profitMarginPercentage || 0), 0) / scenarios?.length;

    return {
      scenarios,
      averages: { avgSellPrice, avgTotalCost, avgGrossProfit, avgMargin }
    };
  }, [selectedScenarios, filteredEstimates]);

  const handleToggleScenario = (id) => {
    setSelectedScenarios(prev => {
      if (prev?.includes(id)) {
        return prev?.filter(sid => sid !== id);
      }
      if (prev?.length >= 4) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleClearSelection = () => {
    setSelectedScenarios([]);
  };

  const formatCurrency = (value) => {
    return `OMR ${parseFloat(value || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
  };

  const formatPercent = (value) => {
    return `${value?.toFixed(1)}%`;
  };

  const getVarianceColor = (variance) => {
    if (variance > 10) return 'text-success';
    if (variance < -10) return 'text-error';
    return 'text-warning';
  };

  const calculateVariance = (value, average) => {
    if (!average) return 0;
    return ((value - average) / average) * 100;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
            <Icon name="GitCompare" size={20} className="text-primary" />
            Scenario Comparison Matrix
          </h3>
          <p className="text-sm text-muted-foreground">
            Compare up to 4 saved scenarios side-by-side with variance analysis
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e?.target?.value)}
            className="text-sm"
          >
            <option value="all">All Time</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="6months">Last 6 Months</option>
            <option value="1year">Last Year</option>
          </Select>
          <Select
            value={projectTypeFilter}
            onChange={(e) => setProjectTypeFilter(e?.target?.value)}
            className="text-sm"
          >
            <option value="all">All Types</option>
            <option value="onsite">Onsite</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </Select>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="text-sm"
          >
            <option value="date">Sort by Date</option>
            <option value="value">Sort by Value</option>
            <option value="margin">Sort by Margin</option>
          </Select>
        </div>
      </div>
      {filteredEstimates?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="FileSearch" size={48} className="text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No scenarios found with current filters</p>
        </div>
      ) : (
        <>
          {/* Scenario Selection List */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-foreground">
                Select Scenarios ({selectedScenarios?.length}/4)
              </p>
              {selectedScenarios?.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="text-xs"
                >
                  Clear Selection
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-64 overflow-y-auto scrollbar-custom">
              {filteredEstimates?.slice(0, 20)?.map((estimate) => (
                <button
                  key={estimate?.id}
                  onClick={() => handleToggleScenario(estimate?.id)}
                  disabled={!selectedScenarios?.includes(estimate?.id) && selectedScenarios?.length >= 4}
                  className={`p-3 rounded-lg border text-left transition-all duration-smooth ${
                    selectedScenarios?.includes(estimate?.id)
                      ? 'border-primary bg-primary/10 shadow-md'
                      : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
                  } ${!selectedScenarios?.includes(estimate?.id) && selectedScenarios?.length >= 4 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-medium text-foreground truncate flex-1">
                      {estimate?.name || 'Unnamed Estimate'}
                    </p>
                    {selectedScenarios?.includes(estimate?.id) && (
                      <Icon name="CheckCircle2" size={16} className="text-primary flex-shrink-0 ml-2" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {format(parseISO(estimate?.timestamp), 'MMM dd, yyyy')}
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {formatCurrency(estimate?.calculations?.sellPrice || 0)}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Comparison Matrix */}
          {comparisonData && comparisonData?.scenarios?.length >= 2 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-foreground border-b border-border">
                        Metric
                      </th>
                      {comparisonData?.scenarios?.map((scenario, idx) => (
                        <th key={scenario?.id} className="text-center p-3 text-sm font-semibold text-foreground border-b border-border">
                          <div className="flex flex-col items-center gap-1">
                            <span className="truncate max-w-[150px]">{scenario?.name || `Scenario ${idx + 1}`}</span>
                            <span className="text-xs font-normal text-muted-foreground">
                              {format(parseISO(scenario?.timestamp), 'MMM dd')}
                            </span>
                          </div>
                        </th>
                      ))}
                      <th className="text-center p-3 text-sm font-semibold text-accent border-b border-border">
                        Average
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Sell Price Row */}
                    <tr className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm font-medium text-foreground border-b border-border">
                        Sell Price
                      </td>
                      {comparisonData?.scenarios?.map((scenario) => {
                        const variance = calculateVariance(
                          scenario?.calculations?.sellPrice,
                          comparisonData?.averages?.avgSellPrice
                        );
                        return (
                          <td key={scenario?.id} className="p-3 text-center border-b border-border">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm font-semibold text-foreground">
                                {formatCurrency(scenario?.calculations?.sellPrice || 0)}
                              </span>
                              <span className={`text-xs font-medium ${getVarianceColor(variance)}`}>
                                {variance > 0 ? '+' : ''}{variance?.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-3 text-center border-b border-border">
                        <span className="text-sm font-semibold text-accent">
                          {formatCurrency(comparisonData?.averages?.avgSellPrice)}
                        </span>
                      </td>
                    </tr>

                    {/* Total Cost Row */}
                    <tr className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm font-medium text-foreground border-b border-border">
                        Total Cost
                      </td>
                      {comparisonData?.scenarios?.map((scenario) => {
                        const variance = calculateVariance(
                          scenario?.calculations?.totalCost,
                          comparisonData?.averages?.avgTotalCost
                        );
                        return (
                          <td key={scenario?.id} className="p-3 text-center border-b border-border">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm font-semibold text-foreground">
                                {formatCurrency(scenario?.calculations?.totalCost || 0)}
                              </span>
                              <span className={`text-xs font-medium ${getVarianceColor(-variance)}`}>
                                {variance > 0 ? '+' : ''}{variance?.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-3 text-center border-b border-border">
                        <span className="text-sm font-semibold text-accent">
                          {formatCurrency(comparisonData?.averages?.avgTotalCost)}
                        </span>
                      </td>
                    </tr>

                    {/* Gross Profit Row */}
                    <tr className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm font-medium text-foreground border-b border-border">
                        Gross Profit
                      </td>
                      {comparisonData?.scenarios?.map((scenario) => {
                        const variance = calculateVariance(
                          scenario?.calculations?.grossProfit,
                          comparisonData?.averages?.avgGrossProfit
                        );
                        return (
                          <td key={scenario?.id} className="p-3 text-center border-b border-border">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm font-semibold text-foreground">
                                {formatCurrency(scenario?.calculations?.grossProfit || 0)}
                              </span>
                              <span className={`text-xs font-medium ${getVarianceColor(variance)}`}>
                                {variance > 0 ? '+' : ''}{variance?.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-3 text-center border-b border-border">
                        <span className="text-sm font-semibold text-accent">
                          {formatCurrency(comparisonData?.averages?.avgGrossProfit)}
                        </span>
                      </td>
                    </tr>

                    {/* Profit Margin Row */}
                    <tr className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm font-medium text-foreground border-b border-border">
                        Profit Margin
                      </td>
                      {comparisonData?.scenarios?.map((scenario) => {
                        const variance = calculateVariance(
                          scenario?.calculations?.profitMarginPercentage,
                          comparisonData?.averages?.avgMargin
                        );
                        return (
                          <td key={scenario?.id} className="p-3 text-center border-b border-border">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm font-semibold text-foreground">
                                {formatPercent(scenario?.calculations?.profitMarginPercentage || 0)}
                              </span>
                              <span className={`text-xs font-medium ${getVarianceColor(variance)}`}>
                                {variance > 0 ? '+' : ''}{variance?.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-3 text-center border-b border-border">
                        <span className="text-sm font-semibold text-accent">
                          {formatPercent(comparisonData?.averages?.avgMargin)}
                        </span>
                      </td>
                    </tr>

                    {/* Delivery Mode Row */}
                    <tr className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm font-medium text-foreground">
                        Delivery Mode
                      </td>
                      {comparisonData?.scenarios?.map((scenario) => (
                        <td key={scenario?.id} className="p-3 text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
                            {scenario?.deliveryMode || 'onsite'}
                          </span>
                        </td>
                      ))}
                      <td className="p-3 text-center">
                        <span className="text-xs text-muted-foreground">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScenarioComparisonMatrix;